import classNames from 'classnames';
import React from 'react';
import ProfilePicture from '@/components/molecules/profile-picture/profile-picture';
import Tooltip, { TooltipPosition } from '../tooltip/tooltip';

export interface BasicPerson {
  /** The first name to show */
  firstName: string;
  /** Whether the border should be dashed */
  hasDashedBorder?: boolean | null;
  /** The profile picture url */
  imageUrl?: string | null;
  /** The last name to show */
  lastName: string;
  /** The placeholders to show in case not having first and last names */
  placeholders?: Content.Properties.PassengerPlaceholder;
}

export interface UserAvatarProps {
  /** The class name to apply */
  className?: string;
  /** Disables the click event on tooltip */
  disableTooltipClick?: boolean;
  /** Tooltip text to show if used as an overflow avatar */
  overflowText?: string;
  /** The passenger information */
  passenger: BasicPerson;
  /** Indicates the size of the avatar */
  size?: 'small' | 'medium' | 'large';
  /** The position of the tooltip */
  tooltipPosition?: TooltipPosition;
}

const UserAvatar = ({
  className,
  disableTooltipClick,
  overflowText,
  passenger,
  size = 'large',
  tooltipPosition,
}: UserAvatarProps) => {
  const fullName = `${passenger.firstName} ${passenger.lastName}`;

  const avatarSizes = {
    large: 32,
    medium: 24,
    small: 16,
  } as const;

  const fontSizes = {
    large: 12,
    medium: 12,
    small: 8,
  } as const;

  return (
    <Tooltip
      className={classNames(className)}
      disableTooltipClick={disableTooltipClick}
      position={tooltipPosition}
      text={overflowText ? overflowText : fullName}
      useMinimumWidth={!!overflowText}
      wrapChildrenOnly
    >
      <ProfilePicture
        firstName={passenger.firstName}
        hasDashedBorder={passenger.hasDashedBorder ?? false}
        imageUrl={passenger.imageUrl ?? undefined}
        initialsFontSize={fontSizes[size]}
        lastName={passenger.lastName}
        mode="userAvatar"
        size={avatarSizes[size]}
      />
    </Tooltip>
  );
};

UserAvatar.displayName = 'UserAvatar';

export default UserAvatar;
